/**
 * FishPrawnCrab orchestrator. Mirrors the imperative side-effect logic in
 *   packages/amgfishprawncrab_v2/lib/presentation/screens/game_screen.dart.
 *
 * Input flow (driven by Rive events on the betting + main artboards):
 *   placeBetClick {betID}     → tryPlaceBet
 *   evtClickChips {chipsAmt}  → selectChip (1..6 from Rive → 0..5 here)
 *   evtClearClick             → clearAll
 *   evtButtonPlayClick        → confirm (single-player)
 *   evtSoundOnOff / evtMusicOnOff → forwarded to setStatus only (no audio in port)
 *
 * Round flow (single-player):
 *   1. user places chips
 *   2. confirm → REST /bet → parsed dice → noMoreBet panel
 *   3. startDiceReveal (dice bowl position=2 + trigger)
 *   4. Rive DiceBowlEnd 'OutEnd' → chips moveAllToCenter, then 1500 ms later distribute
 *   5. Show win/lose DOM panel → reset to betting
 *
 * Round flow (multiplayer):
 *   StartGame   → reset + new round
 *   StartBet    → betting open + countdown + startBet popup
 *   countdown 0 → batched bet submission (submitMultiplayerBets; EndBet is
 *                 the idempotent fallback)
 *   EndBet      → noMoreBet popup, betting closed
 *   StartPlay   → parse Result.dice → startDiceReveal once noMoreBet panel hides
 *   Result      → store winLose + post-round balance → show DOM panel
 *                 (balance settles when the panel appears) → reset
 */
import type { GameRound } from '../types/game';
import { isValidBetId } from '../types/game';
import {
  DEFAULT_AGENT_ID,
  DEFAULT_GAME_CONFIG,
  DEFAULT_PASSWORD,
  GAME_KEY_MULTI,
  GAME_KEY_SINGLE,
  OUTEND_SAFETY_MS,
  PAYOUT_DELAY_MS,
} from '../config';
import type { Api } from '../net/api';
import { ApiError } from '../net/api';
import type { GameState, GameStateSnapshot } from './gameState';
import type { RiveLayer } from '../rive/riveLayer';
import type { ChipLayer } from '../pixi/chipLayer';
import type { Hud } from '../ui/hud';
import type { TableControls } from '../ui/controls';
import type { UrlParams } from './urlParams';
import type { MultiplayerHub, HubEvent } from '../net/multiplayerHub';
// showModeSelectionDialog disabled — mode now comes from the launch URL
// (`g` game key / path; see urlParams.resolveGameMode). Kept importable from
// '../ui/modeSelectionDialog' if re-enabled.
import { type GameMode } from '../ui/modeSelectionDialog';
import type { GameTimerWidget } from '../ui/timerWidget';
import type { GameResultPanel } from '../ui/gameResultPanel';
import type { SoundManager } from '../audio/soundManager';
import { WIN_SFX, LOSE_SFX, COLLECT_CHIPS_SFX } from '../audio/soundWiring';
import { parseResultString, parseHistoryResultValue } from './resultParser';
import { GameLogManager } from './gameLogManager';
import { debugLog, error, warn } from '../util/logger';

/**
 * Multiplayer only: end the visible bet countdown this many seconds before
 * the server's real betting-window close. The batched bet submission fires
 * when the countdown reaches 0, so this buffer gives it time to reach the
 * backend before betting closes and the round starts playing.
 */
const BET_SUBMIT_BUFFER_SEC = 2;

interface OrchestratorPanels {
  startBet: GameResultPanel;
  noMoreBet: GameResultPanel;
  win: GameResultPanel;
  lose: GameResultPanel;
}

interface OrchestratorDeps {
  state: GameState;
  api: Api;
  rive: RiveLayer;
  chips: ChipLayer;
  hud: Hud;
  controls: TableControls;
  hub: MultiplayerHub;
  timer: GameTimerWidget;
  panels: OrchestratorPanels;
  params: UrlParams;
  sound: SoundManager;
  onInteractive?: () => void;
}

export class GameOrchestrator {
  private readonly d: OrchestratorDeps;
  private gid: string = '';

  // Round-flow guard flags (mirror game_screen.dart _isBettingLocked /
  // _noMoreBetEnded / _hasShownStartBetPanelForRound /
  // _hasHandledResultPanelForRound). Reset every round.
  private didStartReveal = false;
  private noMoreBetEnded = false;
  private didDistributeChips = false;
  private didShowResultPanel = false;
  private didResetAfterResult = false;
  private pendingDice: [number, number, number] | null = null;
  private pendingResult: {
    totalWinLose: number;
    winningBetIds: Set<number>;
    hasPositiveWin: boolean;
  } | null = null;
  // Balance settlement is deferred to the WIN/LOSE panel reveal so the
  // displayed (available) balance stays "bet deducted" through the dice
  // reveal and only reflects winnings once the result is shown.
  private pendingSettleBalance: number | null = null;
  private didSettle = false;
  // Multiplayer: the result reveal reached the settle point but the server's
  // Result event (which carries the post-round balance) hasn't arrived yet.
  // We hold the bets in place until it does (see applySettlement) so the
  // displayed available balance never flashes the pre-settlement wallet.
  private awaitingSettleBalance = false;
  // Bumped each round start; settlement captures it before the async balance
  // fetch and bails if a new round started meanwhile.
  private roundToken = 0;
  // Guards applyResultSettlement to once per round.
  private didApplyResultSettlement = false;
  // Multiplayer bets are NOT sent per-chip; they're batched and submitted
  // when the bet countdown ends, so the player can still clear beforehand.
  private didSubmitMpBets = false;
  private sequenceTimers: number[] = [];

  // Multiplayer countdowns.
  private betCountdownHandle: number | null = null;
  private restartCountdownHandle: number | null = null;
  private lastMpEventIndex: number | null = null;

  constructor(deps: OrchestratorDeps) {
    this.d = deps;
  }

  async start(): Promise<void> {
    this.d.state.on((next, prev) => this.onStateChange(next, prev));
    this.bindRiveEvents();
    this.bindHubEvents();
    this.d.controls.bindChips(this.d.state.config);
    this.d.controls.setSelectedChip(this.d.state.selectedChipIndex);
    this.d.controls.setControlsEnabled(false, false);

    this.d.hud.setUserName(this.d.params.userName);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting…');

    try {
      await this.signIn();
      await this.loadConfigAndBalance();
      this.d.onInteractive?.();

      // Mode selection dialog disabled — the mode is now derived from the
      // launch URL the lobby routes to (single `/?g=FPC1` vs multi
      // `/fishprawncrabhub?g=FPCM`; see urlParams.resolveGameMode) instead of
      // prompting the player at launch. Original dialog code kept below
      // (commented) for reference.
      //
      // // Mode selection — mirrors game_screen.dart:972-1020.
      // this.d.state.setPhase('modeSelection');
      // this.d.hud.setStatus('Select mode');
      // const mode: GameMode = await showModeSelectionDialog();
      // debugLog('[Orchestrator] mode selected:', mode);
      const mode: GameMode = this.d.params.gameMode;
      debugLog('[Orchestrator] mode from launch URL (g/path):', mode);

      // Start the dice bowl idle animation now that the mode is known.
      this.d.rive.startDiceShaking();
      this.d.rive.playGirlAnimation(1);

      if (mode === 'multi') {
        this.d.controls.setPlayClearVisible(false);
        await this.startMultiplayer();
      } else {
        this.startBettingRound();
      }
    } catch (e) {
      error('[Orchestrator] start failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.hud.setStatus('ERROR: ' + extractMessage(e));
    }
  }

  // =================================================================
  // Bootstrap
  // =================================================================

  private async signIn(): Promise<void> {
    const { params } = this.d;
    const agentId = params.agentId || DEFAULT_AGENT_ID;

    let bearerToken: string;
    let gsid: string;

    if (params.token && params.gsid) {
      debugLog('[Orchestrator] Using token+gsid from URL params (no login)');
      bearerToken = ensureUrlEncoded(params.token);
      gsid = params.gsid;
    } else {
      this.d.hud.setStatus('Logging in…');
      const login = await this.d.api.login({
        userName: params.userName,
        agentId,
        password: DEFAULT_PASSWORD,
      });
      if (!login.sessionId) throw new Error('Login response missing SessionId');

      this.d.api.setCredentials({
        sessionToken: login.sessionId,
        gameSessionId: null,
        agentId: login.agentId || agentId,
        userName: params.userName,
      });

      this.d.hud.setStatus('Opening game…');
      const session = await this.d.api.openGame({
        userName: params.userName,
        agentId: login.agentId || agentId,
        gameKey: GAME_KEY_SINGLE,
      });

      const parsed = parseGameUrl(session.currentGameUrl, login.sessionId);
      bearerToken = ensureUrlEncoded(parsed.gameToken);
      gsid = parsed.gsid || session.sessionId;

      this.d.state.setBalance(session.balance);
      this.applyServerConfig(session.chips, session.minBet, session.maxBet);
    }

    this.d.api.setCredentials({
      sessionToken: bearerToken,
      gameSessionId: gsid,
      agentId,
      userName: params.userName,
    });
    this.gid = gsid;

    this.d.hud.setStatus('Fetching balance…');
    try {
      const balance = await this.d.api.checkBalance({ sessionId: gsid });
      this.d.state.setBalance(balance);
    } catch (e) {
      warn('[Orchestrator] checkBalance failed (continuing):', e);
    }
  }

  private async loadConfigAndBalance(): Promise<void> {
    const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
    try {
      const info = await this.d.api.getInfo({ sessionId });
      if (info.balance > 0) this.d.state.setBalance(info.balance);
      this.applyServerConfig(info.chips, info.minBet, info.maxBet);
    } catch (e) {
      warn('[Orchestrator] getInfo failed (continuing with defaults):', e);
      if (this.d.state.config.chipValues.length === 0) {
        this.d.state.setConfig({
          minBet: DEFAULT_GAME_CONFIG.minBet,
          maxBet: DEFAULT_GAME_CONFIG.maxBet,
          chipValues: [...DEFAULT_GAME_CONFIG.chipValues],
        });
      }
    }
  }

  private applyServerConfig(chips: number[], minBet: number, maxBet: number): void {
    const current = this.d.state.config;
    const nextChips = chips.length > 0 ? chips : [...current.chipValues];
    const nextMin = minBet > 0 ? minBet : current.minBet;
    const nextMax = maxBet > 0 ? maxBet : current.maxBet;
    debugLog('[Orchestrator] applyServerConfig chips=', nextChips, 'min=', nextMin, 'max=', nextMax);
    this.d.state.setConfig({ minBet: nextMin, maxBet: nextMax, chipValues: nextChips });
    this.d.rive.setBetLimitsText(nextMin, nextMax);
  }

  // =================================================================
  // Rive event bindings
  // =================================================================

  private bindRiveEvents(): void {
    this.d.rive.on('placeBetClick', (payload) => {
      const id = (payload.betID ?? payload.betId ?? payload.id) as number | undefined;
      if (typeof id === 'number' && isValidBetId(id)) this.tryPlaceBet(id);
    });

    this.d.rive.on('evtClickChips', (payload) => {
      // The .riv emits 1..6 directly (chipsAmt). Convert to 0-indexed.
      const i = (payload.chipsAmt ?? payload.chipIndex) as number | undefined;
      if (typeof i === 'number' && i >= 1) this.d.state.selectChip(i - 1);
    });

    this.d.rive.on('evtClearClick', () => this.clearAll());
    this.d.rive.on('evtButtonPlayClick', () => void this.confirm());
    this.d.rive.on('evtSoundOnOff', () => debugLog('[Orchestrator] sound toggle (no-op)'));
    this.d.rive.on('evtMusicOnOff', () => debugLog('[Orchestrator] music toggle (no-op)'));

    // UI button parts (from main artboard "girl" / decoration animations).
    this.d.rive.on('UIButtonClick', (payload) => {
      const parts = String(payload.parts ?? '');
      if (parts === 'startEnd') {
        this.d.rive.playGirlAnimation(1);
      } else if (parts === 'OutEnd') {
        this.d.rive.playGirlAnimation(2);
      } else if (parts === 'noMoreBet_End') {
        // NoMoreBet animation ended — release the gate to start dice reveal.
        this.onNoMoreBetEnded();
      }
    });

    // Dice bowl animation end events.
    this.d.rive.on('DiceBowlEnd', (payload) => {
      const parts = String(payload.parts ?? '');
      if (parts === 'OutEnd') {
        // Reveal finished — animate chips and show win/lose.
        this.onDiceRevealComplete();
      } else if (parts === 'startEnd') {
        // Dice bowl returned to idle for next round.
        debugLog('[Orchestrator] DiceBowlEnd startEnd');
      }
    });
  }

  // =================================================================
  // Player actions
  // =================================================================

  tryPlaceBet(betItemId: number): void {
    const s = this.d.state;
    if (s.isMultiplayer) {
      if (!s.multiplayerCanBet) return;
    } else {
      if (s.phase !== 'betting') return;
    }

    const chip = s.selectedChipValue;
    const chipIndex = s.selectedChipIndex;
    const ok = s.placeBet(betItemId, chip);
    if (!ok) {
      debugLog('[Orchestrator] placeBet rejected', betItemId, chip);
      return;
    }
    // Visual chip flies in immediately and the available balance drops at
    // once (req: balance reflects the bet immediately). Bets are NOT sent to
    // the backend here — single-player submits on Play (confirm) and
    // multiplayer submits when the bet countdown ends — so they stay locally
    // clearable until then (req: player can still clear the bets).
    this.d.chips.placeChip(betItemId, chip, chipIndex);
  }

  clearAll(): void {
    const s = this.d.state;
    if (s.isMultiplayer) {
      // Bets stay local until the countdown-end auto-submit locks them
      // (setMultiplayerCanBet(false)) — clearing is allowed until then.
      if (!s.multiplayerCanBet) return;
    } else {
      if (s.phase !== 'betting') return;
    }
    s.clearBets();
    this.d.chips.hardClear();
  }

  async confirm(): Promise<void> {
    const s = this.d.state;
    if (s.isMultiplayer) {
      debugLog('[Orchestrator] confirm() ignored in multiplayer mode');
      return;
    }
    if (s.phase !== 'betting') return;

    const bets = s.bets.toBetLines();
    if (bets.length === 0) return;

    const cfg = s.config;
    const totalBet = s.bets.total;
    if (cfg.minBet > 0 && totalBet < cfg.minBet) {
      this.d.hud.setStatus(`Minimum bet is ${cfg.minBet}`);
      return;
    }
    if (cfg.maxBet > 0 && totalBet > cfg.maxBet) {
      this.d.hud.setStatus(`Maximum bet is ${cfg.maxBet}`);
      return;
    }
    if (s.balance < totalBet) {
      this.d.hud.setStatus('Insufficient balance');
      return;
    }

    s.setPhase('bettingClosed');
    s.setPhase('waitingResult');

    // Show NoMoreBet popup immediately (mirrors game_screen.dart:274 _noMoreBetPanelKey.show()).
    this.d.panels.startBet.hide();
    this.d.panels.noMoreBet.show({ onHide: () => this.onNoMoreBetEnded() });

    try {
      const sid = this.d.api.getCredentials().gameSessionId ?? '';
      const result = await this.d.api.placeBet({
        sessionId: sid,
        bets,
        gid: this.gid || sid,
      });

      const parsed = parseResultString(result.result);
      if (!parsed) throw new Error(`Failed to parse result: '${result.result}'`);

      const winningBetIds = new Set<number>();
      for (const r of result.betResults) {
        if (r.winLose > 0) winningBetIds.add(r.betItemId);
      }

      this.gid = result.nextRoundId || this.gid;
      const round: GameRound = {
        roundId: this.gid,
        nextRoundId: result.nextRoundId,
        dice: parsed.dice,
        historyValue: parsed.historyValue,
        totalWinLose: result.totalWinLose,
        totalBet: s.bets.total,
        positiveWinAmount: result.positiveWinAmount,
        hasPositiveWin: result.hasPositiveWin,
        winningBetIds: Array.from(winningBetIds),
      };
      const balanceBefore = s.balance;
      s.applyRoundResult(round);
      // Defer the balance update to the WIN/LOSE panel reveal so the
      // displayed balance stays "bet deducted" through the dice reveal and
      // only reflects winnings when the result is shown (req 2).
      this.pendingSettleBalance = result.afterBalance;

      GameLogManager.add({
        betAmount: totalBet,
        betDetails: null,
        winLoseAmount: result.totalWinLose,
        balanceBefore,
        balanceAfter: result.afterBalance,
        gameResult: formatDiceResult(parsed.dice),
        roundId: this.gid,
      });

      this.pendingDice = [parsed.dice[0], parsed.dice[1], parsed.dice[2]];
      this.pendingResult = {
        totalWinLose: result.totalWinLose,
        winningBetIds,
        hasPositiveWin: result.hasPositiveWin,
      };
      this.tryStartDiceReveal();
    } catch (e) {
      error('[Orchestrator] placeBet failed:', e);
      const msg = extractMessage(e);
      if (e instanceof ApiError && e.unauthorized) {
        s.setError('Session expired. Please reload.');
      } else {
        s.setError(msg);
      }
      this.d.hud.setStatus('ERROR: ' + msg);
      this.abortRoundUi();
    }
  }

  /**
   * Submit the locally-accumulated bets to the multiplayer hub. Invoked when
   * the bet countdown ends (and, as a fallback, on the server's EndBet) —
   * the multiplayer analog of single-player confirm().
   *
   * Sends ONE PlaceBet per INDIVIDUAL chip placed (NOT one per bet item
   * carrying the summed total). The hub validates each PlaceBet amount against
   * the configured chip denominations and rejects anything else with "Invalid
   * Chip Amount" — so a summed total like 300 (3×100) is refused even though
   * the server would otherwise add the chips up. Replaying the individual
   * chips mirrors the original per-click submission (V2 game_screen.dart sent
   * one PlaceBet per chip) while keeping our deferred/clearable batching. The
   * single-player REST endpoint differs — it DOES accept a per-item total, so
   * confirm() still uses toBetLines(). Idempotent: the didSubmitMpBets guard
   * prevents a double-submit when both the countdown tick and EndBet fire.
   */
  private submitMultiplayerBets(): void {
    const s = this.d.state;
    if (!s.isMultiplayer) return;
    if (this.didSubmitMpBets) return;
    this.didSubmitMpBets = true;
    // Lock betting locally so no further chips/clears slip in after submit.
    s.setMultiplayerCanBet(false);

    // Per-chip, NOT per-bet-item total — see the doc comment above.
    const lines = s.bets.toChipLines();
    debugLog(
      '[Orchestrator] submitMultiplayerBets — submitting',
      lines.length,
      'chip line(s):',
      lines,
    );
    if (lines.length === 0) return;
    for (const line of lines) {
      void this.d.hub.placeBet(line.betItemId, line.amount);
    }
  }

  // =================================================================
  // Phase / animation orchestration
  // =================================================================

  private startBettingRound(): void {
    this.cancelTimers();
    this.roundToken++;
    this.didStartReveal = false;
    this.noMoreBetEnded = false;
    this.didDistributeChips = false;
    this.didShowResultPanel = false;
    this.didResetAfterResult = false;
    this.pendingDice = null;
    this.pendingResult = null;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didSubmitMpBets = false;

    this.d.state.resetForNewRound();
    this.d.chips.hardClear();
    this.d.state.setPhase('betting');

    this.d.rive.startDiceShaking();
    this.d.rive.playGirlAnimation(1);
    // History UI disabled (no Historyicon inputs in current .riv):
    // this.d.rive.setAllHistory(this.d.state.history);

    this.d.panels.noMoreBet.hide();
    this.d.panels.startBet.show();

    this.d.hud.setStatus('Place your bets');
  }

  /** Called when the noMoreBet popup finishes hiding. */
  private onNoMoreBetEnded(): void {
    if (this.noMoreBetEnded) return;
    this.noMoreBetEnded = true;
    this.tryStartDiceReveal();
  }

  private tryStartDiceReveal(): void {
    if (this.didStartReveal) return;
    if (!this.noMoreBetEnded) return;
    if (!this.pendingDice) return;

    this.didStartReveal = true;
    this.d.state.setPhase('diceReveal');
    const [a, b, c] = this.pendingDice;
    this.d.rive.startDiceReveal(a, b, c);

    // Safety net: if DiceBowlEnd 'OutEnd' never arrives, force the
    // payout sequence so the round still completes.
    this.scheduleTimer(() => this.onDiceRevealComplete(), OUTEND_SAFETY_MS);
  }

  /**
   * Dice bowl 'OutEnd' fired (or safety-net). Mirrors game_screen.dart:807-836:
   *   - playGirlAnimation(2)
   *   - flameGame.moveAllToCenter()
   *   - 1500ms later: distribute chips by result + show win/lose panel.
   */
  private onDiceRevealComplete(): void {
    if (this.didDistributeChips) return;
    this.didDistributeChips = true;

    this.d.rive.playGirlAnimation(2);
    this.d.chips.animateAllToCenter();

    this.scheduleTimer(() => this.distributeAndShowResult(), PAYOUT_DELAY_MS);
  }

  private distributeAndShowResult(): void {
    const pr = this.pendingResult;
    if (!pr) {
      // No bets placed — straight back to betting.
      this.resetToBettingIfNeeded();
      return;
    }

    // Chips are being collected/distributed at settlement.
    this.d.sound.play(COLLECT_CHIPS_SFX);

    // Distribute chips. Mirrors game_screen.dart:818-832.
    const total = pr.totalWinLose;
    if (pr.hasPositiveWin && total > 0) {
      this.d.chips.animateAllToPlayer();
    } else if (pr.hasPositiveWin && total <= 0) {
      this.d.chips.animateHalfHalf();
    } else if (!pr.hasPositiveWin && total <= 0) {
      this.d.chips.animateAllToDealer();
    } else {
      this.d.chips.animateHalfHalf();
    }

    this.d.state.setPhase('result');
    this.showWinLosePanel(total);
  }

  private showWinLosePanel(totalWinLose: number): void {
    if (this.didShowResultPanel) return;
    this.didShowResultPanel = true;

    // Reflect the post-round balance now that the result is shown (req 2).
    // Clears the resolved bets in the same commit so the available balance
    // lands exactly on the server's settled value.
    this.applySettlement();

    if (totalWinLose === 0) {
      // Nothing to show (no bet / push) — clean up immediately.
      this.scheduleTimer(() => this.resetToBettingIfNeeded(), 600);
      return;
    }

    if (totalWinLose > 0) {
      this.d.sound.play(WIN_SFX);
      this.d.hud.setStatus(`YOU WIN ${formatCurrency(totalWinLose)}`);
      this.d.panels.win.show({
        amount: totalWinLose,
        onHide: () => this.onWinLosePanelHide(),
      });
    } else {
      this.d.sound.play(LOSE_SFX);
      this.d.hud.setStatus(`YOU LOSE ${formatCurrency(-totalWinLose)}`);
      this.d.panels.lose.show({
        amount: -totalWinLose,
        onHide: () => this.onWinLosePanelHide(),
      });
    }
  }

  /** Mirrors _onWinLosePanelHide in game_screen.dart:156-175. */
  private onWinLosePanelHide(): void {
    // Restart dice bowl shake for next round.
    this.d.rive.startDiceShaking();
    this.resetToBettingIfNeeded();
  }

  /**
   * Apply the deferred balance settlement: set the wallet to the server's
   * post-round value and clear the resolved bets (atomically, to avoid a
   * one-frame flash of an intermediate available balance). Idempotent — safe
   * to call from showWinLosePanel and again from the teardown safety net. If
   * the server balance isn't known yet (a late multiplayer Result), leaves
   * the bets in place; handleResult finalizes the moment the balance arrives.
   */
  private applySettlement(): void {
    if (this.didSettle) return;
    if (this.pendingSettleBalance != null) {
      // Server's post-round balance is known — finalize atomically.
      this.didSettle = true;
      this.d.state.settle(this.pendingSettleBalance);
    } else {
      // Multiplayer: the reveal reached the settle point but the server's
      // Result event (carrying the post-round balance) hasn't arrived yet.
      // Do NOT clear the bets here — the displayed balance is
      // `wallet − pending bets`, so clearing the bets while the wallet still
      // holds the pre-settlement value would make the balance jump UP to the
      // full (pre-bet) wallet for a moment (the "balance flashes to the old
      // value" bug). Instead, leave the bets so the display stays
      // bet-deducted, and let handleResult finalize atomically the instant the
      // balance arrives.
      this.awaitingSettleBalance = true;
    }
  }

  private resetToBettingIfNeeded(): void {
    if (this.didResetAfterResult) return;
    const p = this.d.state.phase;
    if (p !== 'result' && p !== 'showResult' && p !== 'diceReveal' && p !== 'waitingResult') {
      return;
    }
    this.didResetAfterResult = true;

    // Safety net: ensure the balance was settled even if the win/lose panel
    // step was skipped (e.g. the no-bet path or an early teardown).
    this.applySettlement();

    this.cancelTimers();

    // Hide all panels.
    this.d.panels.win.hide();
    this.d.panels.lose.hide();
    this.d.panels.noMoreBet.hide();

    this.d.chips.hardClear();
    // History UI disabled (no Historyicon inputs in current .riv):
    // this.d.rive.setAllHistory(this.d.state.history);

    // In multiplayer the hub's StartGame drives the next-round reset;
    // we only do the local cleanup. Single-player kicks off a new round.
    if (!this.d.state.isMultiplayer) {
      this.scheduleTimer(() => this.startBettingRound(), 200);
    }
  }

  private abortRoundUi(): void {
    this.d.panels.noMoreBet.hide();
    this.d.panels.startBet.show();
    this.pendingDice = null;
    this.pendingResult = null;
    this.noMoreBetEnded = false;
    this.didStartReveal = false;
    this.didDistributeChips = false;
    this.didShowResultPanel = false;
    this.didResetAfterResult = false;
  }

  // =================================================================
  // State-change → output sync
  // =================================================================

  private onStateChange(next: GameStateSnapshot, prev: GameStateSnapshot): void {
    if (next.balance !== prev.balance) {
      this.renderBalance();
    }

    if (next.bets !== prev.bets) {
      this.d.hud.setBetTotal(next.bets.total);
      // Placing or clearing a bet changes the available balance — reflect
      // it immediately (req: balance updates the instant a bet is placed).
      this.renderBalance();
      const canInteract = next.isMultiplayer
        ? next.multiplayerCanBet
        : next.phase === 'betting';
      this.d.controls.setControlsEnabled(canInteract, !next.bets.isEmpty);
    }

    if (next.selectedChipIndex !== prev.selectedChipIndex) {
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (next.phase !== prev.phase || next.multiplayerCanBet !== prev.multiplayerCanBet) {
      const canInteract = next.isMultiplayer
        ? next.multiplayerCanBet
        : next.phase === 'betting';
      this.d.controls.setControlsEnabled(canInteract, !next.bets.isEmpty);
    }

    if (next.config !== prev.config) {
      this.d.controls.bindChips(next.config);
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    // History UI disabled — the current .riv has no `Historyicon{n}` inputs,
    // so calling setAllHistory floods the console with "Could not access an
    // input" errors. Re-enable once the history slots exist in the Rive file.
    // if (next.history !== prev.history) {
    //   this.d.rive.setAllHistory(next.history);
    // }

    if (
      next.multiplayerBetSeconds !== prev.multiplayerBetSeconds ||
      next.multiplayerRestartSeconds !== prev.multiplayerRestartSeconds
    ) {
      if (next.multiplayerBetSeconds != null) {
        this.d.timer.showBetTimer(next.multiplayerBetSeconds);
      } else if (next.multiplayerRestartSeconds != null) {
        this.d.timer.showRestartTimer(next.multiplayerRestartSeconds);
      } else {
        this.d.timer.showBetTimer(null);
      }
    }
  }

  /**
   * Push the available balance (wallet − pending bets) to the HUD. The HUD
   * is the only balance surface in this game — the .riv has no balance text
   * run (only bet limits; see riveLayer.setBetLimitsText).
   */
  private renderBalance(): void {
    this.d.hud.setBalance(this.d.state.availableBalance);
  }

  // =================================================================
  // Multiplayer — hub bootstrap + event router
  // =================================================================

  private async startMultiplayer(): Promise<void> {
    this.d.state.setMultiplayerActive(true);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting hub…');

    try {
      await this.d.hub.connect();

      const encodedToken = this.d.api.getCredentials().sessionToken ?? '';
      const sessionId = encodedToken.includes('%')
        ? decodeURIComponent(encodedToken)
        : encodedToken;
      if (!sessionId) throw new Error('Missing session token for JoinGame');

      debugLog('[Orchestrator] joining hub with gameKey=', GAME_KEY_MULTI);
      await this.d.hub.joinGame(sessionId, GAME_KEY_MULTI);
      await this.d.hub.getMessages();
      this.d.hud.setStatus('Waiting for next round…');
    } catch (e) {
      error('[Orchestrator] multiplayer connect failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.state.setMultiplayerActive(false);
    }
  }

  private bindHubEvents(): void {
    this.d.hub.on((event) => this.handleHubEvent(event));
  }

  private handleHubEvent(event: HubEvent): void {
    const env = this.normalizeEnvelope(event.payload);

    if (env && typeof env.Index === 'number') {
      const idx = env.Index;
      if (this.lastMpEventIndex != null && idx <= this.lastMpEventIndex) return;
      this.lastMpEventIndex = idx;
    }

    if (env && String(env.State ?? '').toLowerCase() === 'restart') {
      this.lastMpEventIndex = null;
    }

    switch (event.name) {
      case '_connection':
        debugLog('[Hub] connection state', event.payload);
        break;
      case 'PlayerJoined':
        this.handlePlayerJoined(env);
        break;
      case 'PlayerLeft':
        this.handlePlayerLeft(env);
        break;
      case 'StartGame':
        this.handleStartGame(env);
        break;
      case 'StartBet':
        this.handleStartBet(env);
        break;
      case 'EndBet':
        this.handleEndBet(env);
        break;
      case 'StartPlay':
        this.handleStartPlay(env);
        break;
      case 'Result':
        this.handleResult(env);
        break;
      case 'PlaceBet':
        this.handlePlaceBetAck(env);
        break;
      case 'GetMessages':
        // Server replays past events; each fires its own server-event handler.
        break;
      default:
        debugLog('[Hub] unhandled event', event.name, env);
    }
  }

  private normalizeEnvelope(payload: unknown): Envelope {
    if (payload == null) return null;
    if (Array.isArray(payload)) {
      for (const item of payload) {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const status = typeof payload[0] === 'string' ? (payload[0] as string) : null;
          if (status) {
            return { _status: status, ...(item as Record<string, unknown>) };
          }
          return item as Record<string, unknown>;
        }
      }
      return null;
    }
    if (typeof payload === 'object') return payload as Envelope;
    return null;
  }

  // -----------------------------------------------------------------

  private handlePlayerJoined(env: Envelope): void {
    if (env?._status === 'Fail') {
      const msg = String(env.Content ?? 'Failed to join multiplayer table');
      error('[Hub] PlayerJoined Fail:', msg);
      this.d.state.setError(msg);
      this.d.hud.setStatus('ERROR: ' + msg);
      this.stopMultiplayer();
      this.d.state.setPhase('modeSelection');
      return;
    }

    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    const balance = extractBalanceForUser(env?.Data, userName);
    if (balance != null) this.d.state.setBalance(balance);

    const game = (env?.Data && typeof env.Data === 'object'
      ? (env.Data as { Game?: unknown }).Game
      : undefined) as Record<string, unknown> | undefined;
    if (game) {
      const chips = parseChipsField(game.Chips ?? game.chips);
      const minBet = asNumber(game.MinBet ?? game.minBet);
      const maxBet = asNumber(game.MaxBet ?? game.maxBet);
      this.applyServerConfig(chips, minBet, maxBet);
    }
  }

  private handlePlayerLeft(env: Envelope): void {
    const msg = String(env?.Content ?? 'You have been disconnected.');
    this.d.state.setPlayerKickedMessage(msg);
    this.stopMultiplayer();
    this.d.state.setPhase('modeSelection');
    this.d.hud.setStatus('Session ended — please reload');
  }

  private handleStartGame(env: Envelope): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    this.cancelTimers();

    this.roundToken++;
    this.didStartReveal = false;
    this.noMoreBetEnded = false;
    this.didDistributeChips = false;
    this.didShowResultPanel = false;
    this.didResetAfterResult = false;
    this.pendingDice = null;
    this.pendingResult = null;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didSubmitMpBets = false;

    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setMultiplayerRestartSeconds(null);
    this.d.state.setMultiplayerDice(null);
    this.d.state.resetForNewRound();
    this.d.chips.hardClear();
    this.d.state.setPhase('newRound');

    // Some StartGame events include balance / config updates.
    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    const balance = extractBalanceForUser(env?.Data, userName);
    if (balance != null) this.d.state.setBalance(balance);

    // Hide all panels.
    this.d.panels.win.hide();
    this.d.panels.lose.hide();
    this.d.panels.noMoreBet.hide();

    this.d.rive.startDiceShaking();
    // History UI disabled (no Historyicon inputs in current .riv):
    // this.d.rive.setAllHistory(this.d.state.history);
  }

  private handleStartBet(env: Envelope): void {
    const seconds = secondsBetween(env?.StartTime, env?.EndTime);
    debugLog('[Hub] StartBet — countdown', seconds, 's');
    // Fresh bet window — allow a new batched submission this round.
    this.didSubmitMpBets = false;
    this.d.state.setPhase('betting');
    this.d.state.setMultiplayerCanBet(true);

    // Some StartBet payloads include balance/config updates.
    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    const balance = extractBalanceForUser(env?.Data, userName);
    if (balance != null) this.d.state.setBalance(balance);

    this.d.panels.noMoreBet.hide();
    this.d.panels.startBet.show();
    this.d.hud.setStatus('Place your bets');
    // End the visible countdown BET_SUBMIT_BUFFER_SEC seconds early so the
    // batched submit (at countdown 0) reaches the backend before the real
    // window closes and play starts.
    this.startBetCountdown(Math.max(0, seconds - BET_SUBMIT_BUFFER_SEC));
  }

  private handleEndBet(_env: Envelope): void {
    this.cancelBetCountdown();
    // Submit now if the local countdown didn't already (server closed the
    // window first). Idempotent via didSubmitMpBets.
    this.submitMultiplayerBets();
    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setPhase('bettingClosed');
    this.d.panels.startBet.hide();
    this.d.panels.noMoreBet.show({ onHide: () => this.onNoMoreBetEnded() });
    this.d.hud.setStatus('No more bets');
  }

  private handleStartPlay(env: Envelope): void {
    const raw = String(env?.Result ?? '');
    if (!raw) return;
    const parsed = parseResultString(raw);
    if (!parsed) {
      warn('[Hub] StartPlay parse failed:', raw);
      return;
    }
    this.d.state.setMultiplayerResultString(raw);
    this.d.state.setMultiplayerDice(parsed.dice);
    this.pendingDice = [parsed.dice[0], parsed.dice[1], parsed.dice[2]];

    // Apply round skeleton — winLose comes via the Result event.
    this.d.state.applyRoundResult({
      roundId: '',
      nextRoundId: '',
      dice: parsed.dice,
      historyValue: parseHistoryResultValue(raw) ?? 0,
      totalWinLose: 0,
      totalBet: this.d.state.bets.total,
      positiveWinAmount: 0,
      hasPositiveWin: false,
      winningBetIds: [],
    });

    this.tryStartDiceReveal();
  }

  private handleResult(env: Envelope): void {
    const state = String(env?.State ?? '').toLowerCase();

    if (state === 'restart' || env?.StartTime || env?.EndTime) {
      const seconds = secondsBetween(env?.StartTime, env?.EndTime);
      if (seconds > 0) {
        this.d.state.setMultiplayerGameState('restart');
        this.startRestartCountdown(seconds);
        return;
      }
    }

    // The win/lose reveal is driven by the Rive DiceBowlEnd 'OutEnd' panel
    // event (→ onDiceRevealComplete → showWinLosePanel), NOT by handleResult,
    // so there's no result-sequence to start here. Settle off the AUTHORITATIVE
    // post-round balance: the backend stopped returning a usable per-bet figure
    // (WinLost is 0 even on a loss) and the Result event no longer carries a
    // per-user Balance, so the wallet can't be derived locally — we read it from
    // the server (/action/balance). Async.
    void this.settleFromResult(env);
  }

  /**
   * Resolve the player's authoritative post-round balance, then run settlement.
   *
   * Balance source, in order of preference:
   *   1. The Result event's per-user `Balance` field — used when the server
   *      still sends it.
   *   2. /action/balance (checkBalance) — the backend now omits Balance from the
   *      Result event AND returns WinLost=0 even on a loss, so neither the wallet
   *      nor the net P&L can be derived locally; the only correct source is the
   *      server's own balance. (/action/getinfo carries config only — no
   *      balance — so /action/balance is the right endpoint.)
   *
   * Async because of the balance round-trip, so it captures the round token up
   * front and bails if a new round started before the balance arrived — a slow
   * fetch must never settle a finished round into the next round's wallet.
   */
  private async settleFromResult(env: Envelope): Promise<void> {
    const token = this.roundToken;
    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    const winLose = extractWinLoseForUser(env?.Data, userName);
    let balance = extractBalanceForUser(env?.Data, userName);

    if (balance == null) {
      // Server didn't include the post-round balance — fetch it from
      // /action/balance.
      balance = await this.fetchLatestBalance();
    }

    if (token !== this.roundToken) {
      debugLog(
        '[Hub] Result settlement dropped — round advanced during balance fetch',
      );
      return;
    }

    this.applyResultSettlement(winLose, balance);
  }

  /**
   * Fetch the latest wallet balance from the backend via /action/balance
   * (checkBalance) — the same endpoint the startup balance fetch uses.
   * (/action/getinfo carries config only, no balance.) Returns null on failure
   * so the caller can fall back to the locally-tracked wallet rather than
   * zeroing the balance on a transient network error.
   */
  private async fetchLatestBalance(): Promise<number | null> {
    try {
      const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
      const balance = await this.d.api.checkBalance({ sessionId });
      debugLog('[Hub] /action/balance post-result balance =', balance);
      return balance;
    } catch (e) {
      warn('[Hub] checkBalance after result failed — keeping local balance:', e);
      return null;
    }
  }

  /**
   * Apply the resolved post-round balance: settle the wallet, update the round
   * with the win/lose total, log it, and (if the reveal already finished) show
   * the win/lose panel. `balance` is the authoritative post-round wallet (from
   * the Result event or /action/balance); null only when both sources failed,
   * in which case we fall back to `balanceBefore + winLose` (the legacy local
   * computation).
   */
  private applyResultSettlement(winLose: number, balance: number | null): void {
    // Once per round — a retransmitted settlement Result must not re-run this.
    if (this.didApplyResultSettlement) return;
    this.didApplyResultSettlement = true;

    debugLog('[Hub] Result settlement — winLose=', winLose, 'balance=', balance);

    const balanceBefore = this.d.state.balance;
    const totalBet = this.d.state.bets.total;
    const balanceAfter = balance ?? balanceBefore + winLose;
    this.d.state.setMultiplayerWinLose(winLose);
    // Defer the balance update to the WIN/LOSE panel reveal, like
    // single-player (req 2).
    this.pendingSettleBalance = balanceAfter;
    if (this.awaitingSettleBalance && !this.didSettle) {
      // The reveal already reached the settle point and was only waiting on
      // this balance — finalize now (atomic balance update + bet clear) so
      // the display never flashes the pre-settlement wallet.
      this.didSettle = true;
      this.awaitingSettleBalance = false;
      this.d.state.settle(balanceAfter);
    } else if (this.didSettle) {
      // Settlement already applied (a late duplicate Result) — sync the
      // wallet so the server's balance isn't lost. Bets are already cleared.
      this.d.state.setBalance(balanceAfter);
    }

    // Update round with the win/lose total. Without bet-level breakdown
    // from the hub, we treat the result as winning/losing on aggregate.
    const cur = this.d.state.currentRound;
    if (cur) {
      this.d.state.applyRoundResult({
        ...cur,
        totalWinLose: winLose,
        positiveWinAmount: winLose > 0 ? winLose : 0,
        hasPositiveWin: winLose > 0,
      });
      GameLogManager.add({
        betAmount: totalBet,
        betDetails: null,
        winLoseAmount: winLose,
        balanceBefore,
        balanceAfter,
        gameResult: formatDiceResult(cur.dice),
        roundId: cur.roundId || null,
      });
      this.pendingResult = {
        totalWinLose: winLose,
        winningBetIds: new Set<number>(),
        hasPositiveWin: winLose > 0,
      };
    }

    // If dice reveal already finished and we just got the settlement, run
    // the distribution + win/lose panel. If reveal is still in progress,
    // onDiceRevealComplete will pick up `pendingResult` automatically.
    if (this.didStartReveal && !this.didDistributeChips) {
      // Reveal hasn't completed yet — wait for DiceBowlEnd 'OutEnd'.
    } else if (this.didStartReveal && this.didDistributeChips && !this.didShowResultPanel) {
      // Distribute already ran with no result — just show the panel now.
      this.showWinLosePanel(winLose);
    } else if (!this.didStartReveal) {
      // Edge case: Result arrived before StartPlay — wait for reveal.
    }
  }

  /**
   * PlaceBet ack — server confirmed (part of) our batched bet submission.
   *
   * No-op for balance: bets are submitted as a single batch when the
   * countdown ends and the displayed balance is optimistic (wallet −
   * pending bets). The authoritative wallet is re-synced from the Result
   * event at settlement, so applying the ack's per-bet balance here would
   * double-count the deduction.
   */
  private handlePlaceBetAck(_env: Envelope): void {
    // intentionally empty (see above)
  }

  // -----------------------------------------------------------------

  private startBetCountdown(seconds: number): void {
    this.cancelBetCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerBetSeconds(remaining);
    this.betCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelBetCountdown();
        this.d.state.setMultiplayerBetSeconds(null);
        // Countdown ended — submit the batched bets now (req 3: multiplayer
        // submits when the countdown ends, not per-chip).
        this.submitMultiplayerBets();
      } else {
        this.d.state.setMultiplayerBetSeconds(remaining);
      }
    }, 1000);
  }

  private cancelBetCountdown(): void {
    if (this.betCountdownHandle != null) {
      window.clearInterval(this.betCountdownHandle);
      this.betCountdownHandle = null;
    }
  }

  private startRestartCountdown(seconds: number): void {
    this.cancelRestartCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerRestartSeconds(remaining);
    this.restartCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelRestartCountdown();
        this.d.state.setMultiplayerRestartSeconds(null);
      } else {
        this.d.state.setMultiplayerRestartSeconds(remaining);
      }
    }, 1000);
  }

  private cancelRestartCountdown(): void {
    if (this.restartCountdownHandle != null) {
      window.clearInterval(this.restartCountdownHandle);
      this.restartCountdownHandle = null;
    }
  }

  private stopMultiplayer(): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    void this.d.hub.disconnect();
    this.d.state.setMultiplayerActive(false);
  }

  // =================================================================
  // Helpers
  // =================================================================

  private scheduleTimer(fn: () => void, ms: number): void {
    const id = window.setTimeout(fn, ms);
    this.sequenceTimers.push(id);
  }

  private cancelTimers(): void {
    for (const id of this.sequenceTimers) window.clearTimeout(id);
    this.sequenceTimers = [];
  }
}

// =====================================================================
// Free helpers
// =====================================================================

function formatCurrency(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function formatDiceResult(dice: readonly [number, number, number]): string {
  return `Dice ${dice[0]}-${dice[1]}-${dice[2]}`;
}

function ensureUrlEncoded(token: string): string {
  if (!token) return token;
  if (token.includes('%')) return token;
  return encodeURIComponent(token);
}

function parseGameUrl(
  currentGameUrl: string,
  loginToken: string,
): { gameToken: string; gsid: string } {
  if (!currentGameUrl) return { gameToken: loginToken, gsid: '' };
  try {
    const u = new URL(currentGameUrl, 'https://placeholder.invalid');
    const urlToken = u.searchParams.get('token');
    const urlGsid = u.searchParams.get('gsid');
    return {
      gameToken: urlToken && urlToken.length > 0 ? urlToken : loginToken,
      gsid: urlGsid ?? '',
    };
  } catch {
    return { gameToken: loginToken, gsid: '' };
  }
}

function extractMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

type Envelope = Record<string, unknown> | null;

function asNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function parseChipsField(raw: unknown): number[] {
  const out: number[] = [];
  if (typeof raw === 'string') {
    for (const piece of raw.split(',')) {
      const n = parseInt(piece.trim(), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  } else if (Array.isArray(raw)) {
    for (const v of raw) {
      const n = typeof v === 'number' ? v : parseInt(String(v), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  }
  return out;
}

function secondsBetween(start: unknown, end: unknown): number {
  if (typeof start !== 'string' || typeof end !== 'string') return 0;
  const a = Date.parse(start);
  const b = Date.parse(end);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.round((b - a) / 1000);
}

function pickUserName(item: Record<string, unknown>): string {
  const raw = item.Username ?? item.username ?? item.UserName ?? item.userName;
  return raw == null ? '' : String(raw).toLowerCase();
}

function extractWinLoseForUser(data: unknown, userNameLower: string): number {
  if (data == null || !userNameLower) return 0;
  if (Array.isArray(data)) {
    let sum = 0;
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue;
      sum += asNumber(item.WinLost ?? item.WinLose ?? item.winLost ?? item.winLose);
    }
    return sum;
  }
  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (u && u !== userNameLower) return 0;
    return asNumber(m.TotalWinLose ?? m.WinLose ?? m.winLose ?? m.WinLost);
  }
  return 0;
}

function extractBalanceForUser(data: unknown, userNameLower: string): number | null {
  if (data == null || !userNameLower) return null;
  if (Array.isArray(data)) {
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue;
      const b = item.Balance ?? item.balance;
      if (b == null) return null;
      const n = typeof b === 'number' ? b : Number(b);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }
  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (!u || u !== userNameLower) return null;
    const b = m.Balance ?? m.balance;
    if (b == null) return null;
    const n = typeof b === 'number' ? b : Number(b);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
